import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography, Button, Grid } from "@mui/material";
import FrequencySlider from "../controls/FrequencySlider";
import VerticalVolumeSlider from "../controls/VerticalVolumeSlider";

export default function AudioTone({ frequency = 1000, volume = -10, onFrequencyChange, onVolumeChange }) {
   const audioContextRef = useRef(null);
   const oscillatorRef = useRef(null);
   const gainNodeRef = useRef(null);
   const [isPlaying, setIsPlaying] = useState(false);

   // Ensure frequency and volume are numbers
   const safeFrequency = typeof frequency === 'number' ? frequency : 1000;
   const safeVolume = typeof volume === 'number' ? volume : -10;
   // Ensure callbacks are functions
   const safeOnFrequencyChange = typeof onFrequencyChange === 'function' ? onFrequencyChange : () => {};
   const safeOnVolumeChange = typeof onVolumeChange === 'function' ? onVolumeChange : () => {};

   useEffect(() => {
      // Initialize AudioContext on mount
      if (!audioContextRef.current) {
         audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      return () => {
         // Cleanup on unmount
         stopTone();
         if (audioContextRef.current) {
            audioContextRef.current.close();
         }
      };
   }, []);

   useEffect(() => {
      // Update frequency when it changes
      if (oscillatorRef.current && isPlaying && safeFrequency) {
         oscillatorRef.current.frequency.value = safeFrequency;
      }
   }, [safeFrequency, isPlaying]);

   useEffect(() => {
      // Update volume when it changes
      if (gainNodeRef.current && safeVolume !== undefined) {
         const gain = safeVolume <= -60 ? 0 : Math.pow(10, safeVolume / 20);
         gainNodeRef.current.gain.value = gain;
      }
   }, [safeVolume]);

   const startTone = () => {
      if (!audioContextRef.current) {
         audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Resume audio context if suspended (required by browser autoplay policies)
      if (audioContextRef.current.state === 'suspended') {
         audioContextRef.current.resume();
      }

      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = safeFrequency;

      const gain = safeVolume <= -60 ? 0 : Math.pow(10, safeVolume / 20);
      gainNode.gain.value = gain;

      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      oscillator.start();
      oscillatorRef.current = oscillator;
      gainNodeRef.current = gainNode;
      setIsPlaying(true);
   };

   const stopTone = () => {
      if (oscillatorRef.current) {
         oscillatorRef.current.stop();
         oscillatorRef.current.disconnect();
         oscillatorRef.current = null;
      }
      setIsPlaying(false);
   };

   const toggleTone = () => {
      if (isPlaying) {
         stopTone();
      } else {
         startTone();
      }
   };

   return (
      <Box
         sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#1a1a1a",
            color: "#ffffff",
            position: "absolute",
            top: 0,
            left: 0,
         }}
      >
         <Box sx={{ width: "90%" }}>
            <Grid container spacing={4}>
               {/* Row 1: Hz value (75%) and Volume/Play (25%) */}
               <Grid container item xs={12} spacing={4}>
                  {/* Column 1: Hz value display - 75% width */}
                  <Grid item xs={9}>
                     <Stack
                        spacing={2}
                        sx={{
                           display: "flex",
                           alignItems: "center",
                           justifyContent: "center",
                           height: "100%",
                        }}
                     >
                        <Box
                           sx={{
                              fontSize: "200px",
                              fontWeight: "bold",
                              fontFamily: "monospace",
                              color: "#00ff00",
                              textAlign: "center",
                           }}
                        >
                           {safeFrequency.toLocaleString()} Hz
                        </Box>
                        <Box
                           sx={{
                              fontSize: "100px",
                              fontWeight: "bold",
                              fontFamily: "monospace",
                              color: "#00ff00",
                              textAlign: "center",
                           }}
                        >
                           {safeVolume.toFixed(1)} dB
                        </Box>
                     </Stack>
                  </Grid>
                  
                  {/* Column 2: Volume slider and Play button - 25% width */}
                  <Grid item xs={3}>
                     <Stack spacing={4} alignItems="center" sx={{ height: "100%" }}>
                        <VerticalVolumeSlider
                           label="Volume"
                           value={safeVolume}
                           callback={safeOnVolumeChange}
                        />
                        <Button
                           variant="contained"
                           color={isPlaying ? "error" : "success"}
                           onClick={toggleTone}
                           sx={{ minWidth: 120, fontSize: 18, py: 1.5 }}
                        >
                           {isPlaying ? "Stop" : "Play"}
                        </Button>
                     </Stack>
                  </Grid>
               </Grid>

               {/* Row 2: Frequency slider - 100% width */}
               <Grid item xs={12}>
                  <FrequencySlider
                     label="Frequency"
                     value={safeFrequency}
                     callback={safeOnFrequencyChange}
                  />
               </Grid>
            </Grid>
         </Box>
      </Box>
   );
}
